/**
 * Dynamic Portal Forms — form definitions, admin CRUD payloads, and public
 * submission payloads for the client portal's Form Builder feature.
 *
 * A form's `definition` is stored verbatim as JSON in tenant
 * `portal_forms.definition`; submissions freeze a copy into
 * `portal_form_submissions.form_snapshot` so later edits to the form never
 * corrupt the history of what a client actually filled in (same convention
 * as qm_evaluations.form_snapshot).
 */
import { z, uuidSchema } from '@kc3/validation';
export const PORTAL_FORM_FIELD_TYPES = [
    'text',
    'textarea',
    'email',
    'phone',
    'number',
    'date',
    'dropdown',
    'radio',
    'checkboxes',
    'file',
    // Holds a conversation reference_id (e.g. "KC3-000123"). On submit the API
    // resolves it to a conversation and links the submission, which then
    // appears as an inbound message on that conversation's timeline.
    'conversation_ref',
];
/** Field types that render an options list the admin must maintain. */
export const PORTAL_FORM_OPTION_TYPES = ['dropdown', 'radio', 'checkboxes'];
export const PortalFormFieldOptionSchema = z.object({
    label: z.string().min(1).max(255),
    value: z.string().min(1).max(255),
});
export const PortalFormFieldValidationSchema = z.object({
    minLength: z.number().int().min(0).optional(),
    maxLength: z.number().int().min(1).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    /** Regex source the value must fully satisfy — validated on both client and server. */
    pattern: z.string().max(500).optional(),
    /** Human-readable hint shown when `pattern` fails (e.g. "Use format XXX-XXX"). */
    patternMessage: z.string().max(255).optional(),
    // file-type fields only
    /** MIME types and/or '.ext' tokens accepted by the file input. */
    accept: z.array(z.string().trim().min(1).max(100)).max(40).optional(),
    maxFileSizeMb: z.number().min(0.1).max(100).optional(),
    maxFiles: z.number().int().min(1).max(10).optional(),
});
export const PortalFormFieldSchema = z.object({
    /** Stable key generated at field creation — submission values are keyed by it. */
    id: z.string().min(1).max(64).regex(/^[a-zA-Z0-9_-]+$/, 'Field id must be alphanumeric'),
    type: z.enum(PORTAL_FORM_FIELD_TYPES),
    label: z.string().min(1).max(255),
    placeholder: z.string().max(255).optional(),
    helpText: z.string().max(500).optional(),
    required: z.boolean().default(false),
    /** When true, this field's answer is omitted from the timeline message
     *  posted into a linked conversation (it stays in the stored submission).
     *  Use for sensitive values — ID numbers, phone numbers, etc. */
    excludeFromTimeline: z.boolean().default(false),
    options: z.array(PortalFormFieldOptionSchema).default([]),
    validation: PortalFormFieldValidationSchema.default({}),
});
export const PortalFormDefinitionSchema = z.object({
    fields: z.array(PortalFormFieldSchema).default([]),
    submitLabel: z.string().min(1).max(80).default('Submit'),
    successMessage: z.string().max(1000).default('Thank you! Your form has been submitted.'),
    /** When true, only logged-in client-portal users may submit. */
    requireLogin: z.boolean().default(false),
    /**
     * PV-07: optional marketing-consent tick box above the submit button. Only a ticked box records
     * anything (consent record with this wording, version, IP and browser). Unticked stores nothing.
     */
    marketingConsent: z
        .object({
        enabled: z.boolean().default(false),
        channel: z.enum(['EMAIL', 'SMS', 'WHATSAPP', 'ALL']).default('EMAIL'),
        label: z.string().trim().min(1).max(500).default('I agree to receive marketing messages.'),
        wordingVersion: z.string().trim().min(1).max(40).default('v1'),
    })
        .optional(),
});
// ---------- Admin API ----------
export const PORTAL_FORM_STATUSES = ['active', 'inactive'];
export const PortalFormSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    /** Label shown on the client-portal navigation tab — falls back to `name`. */
    navLabel: z.string().nullable(),
    slug: z.string(),
    description: z.string().nullable(),
    status: z.enum(PORTAL_FORM_STATUSES),
    definition: PortalFormDefinitionSchema,
    version: z.number().int(),
    submissionCount: z.number().int().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreatePortalFormRequestSchema = z.object({
    name: z.string().trim().min(1).max(255),
    navLabel: z.string().trim().max(80).nullable().optional(),
    slug: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase alphanumeric with dashes')
        .optional(),
    description: z.string().max(2000).nullable().optional(),
    status: z.enum(PORTAL_FORM_STATUSES).default('active'),
    definition: PortalFormDefinitionSchema.default({}),
});
export const UpdatePortalFormRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(255).optional(),
    navLabel: z.string().trim().max(80).nullable().optional(),
    slug: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase alphanumeric with dashes')
        .optional(),
    description: z.string().max(2000).nullable().optional(),
    status: z.enum(PORTAL_FORM_STATUSES).optional(),
    definition: PortalFormDefinitionSchema.optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'At least one field must be provided',
});
export const PortalFormListQuerySchema = z.object({
    search: z.string().trim().min(1).optional(),
    status: z.enum(PORTAL_FORM_STATUSES).optional(),
});
export const PortalFormDropdownItemSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    navLabel: z.string().nullable().optional(),
    slug: z.string(),
    status: z.enum(PORTAL_FORM_STATUSES),
});
// ---------- Submissions ----------
export const PortalFormSubmissionSchema = z.object({
    id: uuidSchema,
    formId: uuidSchema,
    formVersion: z.number().int(),
    clientId: uuidSchema.nullable(),
    /** Conversation this submission was linked to via a conversation_ref field. */
    conversationId: uuidSchema.nullable().optional(),
    values: z.record(z.string(), z.unknown()),
    status: z.string(),
    createdAt: z.string().datetime(),
});
/** Public submit body — values keyed by field id; file fields hold attachment id arrays. */
export const SubmitPortalFormRequestSchema = z.object({
    values: z.record(z.string(), z.unknown()).default({}),
    /** PV-07: true only when the visitor ticked the form's marketing-consent box. */
    marketingConsent: z.boolean().optional(),
});
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+()\-.\s\d]{5,32}$/;
function isEmptyValue(value) {
    return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
}
/**
 * Validates submitted values against a form definition. Pure function —
 * runs identically in the API (authoritative, before insert) and in the
 * browser renderer (immediate feedback). File-field values are attachment
 * id arrays; existence/ownership is checked separately by the API.
 */
export function validatePortalFormValues(definition, values) {
    const errors = [];
    for (const field of definition.fields) {
        const value = values[field.id];
        const v = field.validation;
        if (isEmptyValue(value)) {
            if (field.required)
                errors.push({ fieldId: field.id, message: `${field.label} is required` });
            continue;
        }
        switch (field.type) {
            case 'text':
            case 'textarea':
            case 'conversation_ref': {
                if (typeof value !== 'string')
                    break;
                if (v.minLength !== undefined && value.length < v.minLength) {
                    errors.push({ fieldId: field.id, message: `${field.label} must be at least ${v.minLength} characters` });
                }
                if (v.maxLength !== undefined && value.length > v.maxLength) {
                    errors.push({ fieldId: field.id, message: `${field.label} must be at most ${v.maxLength} characters` });
                }
                break;
            }
            case 'email': {
                if (typeof value === 'string' && !EMAIL_RE.test(value)) {
                    errors.push({ fieldId: field.id, message: `${field.label} must be a valid email address` });
                }
                break;
            }
            case 'phone': {
                if (typeof value === 'string' && !PHONE_RE.test(value)) {
                    errors.push({ fieldId: field.id, message: `${field.label} must be a valid phone number` });
                }
                break;
            }
            case 'number': {
                const num = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
                if (!Number.isFinite(num)) {
                    errors.push({ fieldId: field.id, message: `${field.label} must be a number` });
                    break;
                }
                if (v.min !== undefined && num < v.min)
                    errors.push({ fieldId: field.id, message: `${field.label} must be at least ${v.min}` });
                if (v.max !== undefined && num > v.max)
                    errors.push({ fieldId: field.id, message: `${field.label} must be at most ${v.max}` });
                break;
            }
            case 'date': {
                if (typeof value === 'string' && Number.isNaN(new Date(`${value}T00:00:00`).getTime())) {
                    errors.push({ fieldId: field.id, message: `${field.label} must be a valid date` });
                }
                break;
            }
            case 'dropdown':
            case 'radio': {
                const allowed = new Set(field.options.map((o) => o.value));
                if (typeof value === 'string' && !allowed.has(value)) {
                    errors.push({ fieldId: field.id, message: `${field.label} has an invalid selection` });
                }
                break;
            }
            case 'checkboxes': {
                if (!Array.isArray(value)) {
                    errors.push({ fieldId: field.id, message: `${field.label} has an invalid selection` });
                    break;
                }
                const allowed = new Set(field.options.map((o) => o.value));
                if (value.some((entry) => typeof entry !== 'string' || !allowed.has(entry))) {
                    errors.push({ fieldId: field.id, message: `${field.label} has an invalid selection` });
                }
                break;
            }
            case 'file': {
                if (!Array.isArray(value) || value.some((entry) => typeof entry !== 'string')) {
                    errors.push({ fieldId: field.id, message: `${field.label} has invalid attachments` });
                    break;
                }
                if (v.maxFiles !== undefined && value.length > v.maxFiles) {
                    errors.push({ fieldId: field.id, message: `${field.label} allows at most ${v.maxFiles} file${v.maxFiles === 1 ? '' : 's'}` });
                }
                break;
            }
        }
        // Pattern applies to scalar string inputs regardless of type.
        if (v.pattern && typeof value === 'string' && value.length > 0 && field.type !== 'file') {
            try {
                if (!new RegExp(v.pattern).test(value)) {
                    errors.push({ fieldId: field.id, message: v.patternMessage || `${field.label} has an invalid format` });
                }
            }
            catch {
                // Invalid regex in a saved definition — skip rather than block submission.
            }
        }
    }
    return errors;
}
//# sourceMappingURL=portal-forms.js.map